import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { PORTAL, usePortal } from '../portalAuth';
import { STORE_BASE, STORE_HOME } from '../routeBase';

export default function Login() {
  const { login } = usePortal();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('login');      // login | forgot | otp
  // OTP reset flow
  const [otpHint, setOtpHint] = useState('');     // masked email the code went to
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const doLogin = async e => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const r = await PORTAL.post('/login', { identifier, password });
      login(r.data.token, r.data.customer);
      nav(r.data.customer?.must_change_password ? `${STORE_BASE}/force-change-password` : STORE_HOME);
    } catch (e2) { setErr(e2.response?.data?.error || 'Login failed'); }
    setBusy(false);
  };

  // Step 1 of "forgot password" — email a 6-digit code. The backend responds
  // generically whether or not the account exists (no enumeration), so we
  // always move to the code-entry step.
  const requestOtp = async e => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const r = await PORTAL.post('/reset/request-otp', { identifier });
      setOtpHint(r.data.email_hint || '');
      setCode(''); setNewPw(''); setNewPw2('');
      setMode('otp');
      setResendIn(60);
    } catch (e2) { setErr(e2.response?.data?.error || 'Something went wrong'); }
    setBusy(false);
  };

  // Step 2 — verify the code and set the new password. On success the backend
  // returns a session, so we're signed straight in.
  const verifyOtp = async e => {
    e.preventDefault(); setErr('');
    if (newPw.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (newPw !== newPw2) { setErr('Passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await PORTAL.post('/reset/verify-otp', { identifier, code: code.trim(), password: newPw });
      login(r.data.token, r.data.customer);
      nav(`${STORE_BASE}/dashboard`);
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not reset password'); }
    setBusy(false);
  };

  const resendOtp = async () => {
    if (resendIn > 0 || busy) return;
    setErr(''); setBusy(true);
    try {
      const r = await PORTAL.post('/reset/request-otp', { identifier });
      setOtpHint(r.data.email_hint || otpHint);
      setResendIn(60);
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not resend'); }
    setBusy(false);
  };

  return (
    <div style={{ maxWidth: 420, margin: '50px auto', padding: '0 20px' }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>
          {mode === 'login' ? 'Sign in' : mode === 'forgot' ? 'Reset your password' : 'Enter the code'}
        </h1>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 22 }}>
          {mode === 'login' ? 'Access wholesale prices and place orders.'
            : mode === 'forgot' ? 'Enter your registered email or mobile number and we\'ll email you a 6-digit code.'
            : otpHint
              ? <>We emailed a 6-digit code to <strong>{otpHint}</strong>. Enter it below with your new password.</>
              : 'If an account exists, a 6-digit code has been emailed to the address on file. Enter it below with your new password.'}
        </p>

        {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '9px 12px', borderRadius: 8, marginBottom: 14 }}>{err}</div>}

        {mode === 'login' && (
          <form onSubmit={doLogin}>
            <Field label="Email or mobile" value={identifier} onChange={setIdentifier} placeholder="you@company.com or 98765xxxxx" />
            <Field label="Password" type="password" value={password} onChange={setPassword} placeholder="Your password" />
            <button type="submit" disabled={busy} className="sf-btn-outline" style={primaryBtn}>{busy ? 'Signing in…' : 'Sign in'}</button>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
              <button type="button" onClick={() => { setMode('forgot'); setErr(''); }} style={linkBtn}>Forgot / set your password</button>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={requestOtp}>
            <Field label="Email or mobile" value={identifier} onChange={setIdentifier} placeholder="Registered email or mobile" />
            <button type="submit" disabled={busy} className="sf-btn-outline" style={primaryBtn}>{busy ? 'Sending…' : 'Email me a code'}</button>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
              <button type="button" onClick={() => { setMode('login'); setErr(''); }} style={linkBtn}>Back to sign in</button>
            </div>
          </form>
        )}

        {mode === 'otp' && (
          <form onSubmit={verifyOtp}>
            <Field label="6-digit code" value={code} onChange={v => setCode(v.replace(/\D/g, '').slice(0, 6))} placeholder="123456" />
            <Field label="New password" type="password" value={newPw} onChange={setNewPw} placeholder="At least 6 characters" />
            <Field label="Confirm new password" type="password" value={newPw2} onChange={setNewPw2} placeholder="Re-enter password" />
            <button type="submit" disabled={busy} className="sf-btn-outline" style={primaryBtn}>{busy ? 'Saving…' : 'Set password & sign in'}</button>
            <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={() => { setMode('forgot'); setErr(''); }} style={linkBtn}>Use a different email/mobile</button>
              <button type="button" onClick={resendOtp} disabled={resendIn > 0 || busy} style={{ ...linkBtn, opacity: resendIn > 0 ? 0.5 : 1 }}>
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 22, paddingTop: 16, textAlign: 'center', fontSize: 13, color: '#888' }}>
          Don't have an account? <Link to={`${STORE_BASE}/register`} style={{ color: 'var(--sf-accent)', fontWeight: 500 }}>Register</Link>
        </div>
      </div>
    </div>
  );
}

// separate route target for /store/set-password?token=...
export function SetPassword() {
  const { login } = usePortal();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get('token');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (pw.length < 6) return setErr('Password must be at least 6 characters');
    if (pw !== pw2) return setErr('Passwords do not match');
    setBusy(true);
    try {
      const r = await PORTAL.post('/set-password', { token, password: pw });
      login(r.data.token, r.data.customer);
      // Land on the customer's own dashboard (not the storefront home) so the
      // first thing they see after setting a password is their account —
      // orders, profile, addresses.
      nav(`${STORE_BASE}/dashboard`);
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not set password'); }
    setBusy(false);
  };

  if (!token) return <div style={{ padding: 60, textAlign: 'center' }}>Invalid link. <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link></div>;

  return (
    <div style={{ maxWidth: 420, margin: '50px auto', padding: '0 20px' }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 32 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 18 }}>Set your password</h1>
        {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '9px 12px', borderRadius: 8, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={submit}>
          <Field label="New password" type="password" value={pw} onChange={setPw} placeholder="At least 6 characters" />
          <Field label="Confirm password" type="password" value={pw2} onChange={setPw2} placeholder="Re-enter password" />
          <button type="submit" disabled={busy} className="sf-btn-outline" style={primaryBtn}>{busy ? 'Saving…' : 'Set password & sign in'}</button>
        </form>
      </div>
    </div>
  );
}

// route target for /store/force-change-password — shown right after a
// customer logs in with an admin-issued temporary password
// (customer.must_change_password); they must set their own before doing
// anything else in the portal.
export function ForceChangePassword() {
  const { customer, setCustomer } = usePortal();
  const nav = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async e => {
    e.preventDefault(); setErr('');
    if (pw.length < 6) return setErr('Password must be at least 6 characters');
    if (pw !== pw2) return setErr('Passwords do not match');
    setBusy(true);
    try {
      await PORTAL.post('/change-password', { current_password: currentPassword, new_password: pw });
      setCustomer({ ...customer, must_change_password: false });
      nav(STORE_HOME);
    } catch (e2) { setErr(e2.response?.data?.error || 'Could not update password'); }
    setBusy(false);
  };

  return (
    <div style={{ maxWidth: 420, margin: '50px auto', padding: '0 20px' }}>
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 32 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Set a new password</h1>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 22 }}>
          An administrator reset your password. Enter the temporary password you were given, then choose your own.
        </p>
        {err && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '9px 12px', borderRadius: 8, marginBottom: 14 }}>{err}</div>}
        <form onSubmit={submit}>
          <Field label="Temporary password" type="password" value={currentPassword} onChange={setCurrentPassword} placeholder="The password you were given" />
          <Field label="New password" type="password" value={pw} onChange={setPw} placeholder="At least 6 characters" />
          <Field label="Confirm new password" type="password" value={pw2} onChange={setPw2} placeholder="Re-enter password" />
          <button type="submit" disabled={busy} className="sf-btn-outline" style={primaryBtn}>{busy ? 'Saving…' : 'Set Password & Continue'}</button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 12.5, fontWeight: 500, color: '#555', display: 'block', marginBottom: 5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
    </div>
  );
}

const primaryBtn = { width: '100%', padding: '11px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const linkBtn = { background: 'none', border: 'none', color: 'var(--sf-accent)', cursor: 'pointer', fontSize: 13 };
